import React, { Suspense } from 'react';
import './App.css';
import { LoadingPanel } from './shared/components';
import { adminBasePath, parentBasePath, studentBasePath, subscriptionBasePath } from './shared/config';
import AuthLoginLoadingShell from './features/auth/AuthLoginLoadingShell';
import { publicPortalLoginTargetFromLocation } from './features/portal/publicPortalLoginIntent';
import { catalogsForTarget } from './shared/i18n/appCatalogs';
import { I18nProvider, useText } from './shared/i18n/I18nProvider';

const targetApps = {
  admin: React.lazy(() => import('./features/admin/AdminApp')),
  parent: React.lazy(() => import('./features/parent/ParentApp')),
  portal: React.lazy(() => import('./features/portal/PortalApp')),
  student: React.lazy(() => import('./features/student/StudentApp')),
  subscription: React.lazy(() => import('./features/subscription/SubscriptionApp')),
};

export default function App() {
  const appTarget = resolveAppTarget(process.env.REACT_APP_WEBAPP_TARGET || 'portal');
  const TargetApp = targetApps[appTarget] || targetApps.portal;

  return (
    <I18nProvider catalogs={catalogsForTarget(appTarget)}>
      <Suspense fallback={<AppLoadingFallback appTarget={appTarget} />}>
        <TargetApp />
      </Suspense>
    </I18nProvider>
  );
}

export function resolveAppTarget(configuredTarget = 'portal') {
  const target = String(configuredTarget || 'portal').trim();
  if (target && target !== 'portal') {
    return target;
  }
  if (matchesBasePath(parentBasePath)) {
    return 'parent';
  }
  if (matchesBasePath(subscriptionBasePath) || matchesBasePath('/subscription')) {
    return 'subscription';
  }
  if (matchesBasePath(adminBasePath)) {
    return 'admin';
  }
  if (matchesBasePath(studentBasePath)) {
    return 'student';
  }
  return 'portal';
}

function matchesBasePath(basePath) {
  if (typeof window === 'undefined') {
    return false;
  }
  const normalizedBase = normalizeBasePath(basePath);
  if (!normalizedBase) {
    return false;
  }
  const pathname = normalizeBasePath(window.location.pathname) || '/';
  return pathname === normalizedBase || pathname.startsWith(`${normalizedBase}/`);
}

function normalizeBasePath(value) {
  const raw = String(value || '').trim();
  if (!raw || raw === '/') {
    return '';
  }
  return `/${raw.replace(/^\/+/, '').replace(/\/+$/, '')}`;
}

export function AppLoadingFallback({ appTarget = 'portal' }) {
  const t = useText();
  const loginTarget = appTarget === 'portal' ? publicPortalLoginTargetFromLocation() : appTarget;
  const canRenderAuthFallback = ['admin', 'parent', 'student'].includes(loginTarget);
  const reservesSecondaryAction = loginTarget && loginTarget !== 'admin';

  if (canRenderAuthFallback) {
    return <AuthLoginLoadingShell loginTarget={loginTarget} reserveSecondaryAction={reservesSecondaryAction} />;
  }

  return (
    <main className="app-page app-page-centered">
      <section className="card loading-card">
        <LoadingPanel label={t('common.loading.workspace')} />
      </section>
    </main>
  );
}
